/**
 * LDT Order Information Section Component
 *
 * Displays order information sections (Patient, Clinical, Specimen, Billing)
 * and uploaded files
 */
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Grid,
  Stack,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { useLdtFileUploads } from '@ldt/features/orders/hooks/use_ldt_file_uploads';
import {
  createMetastaticProstateClinicCustomResolver,
  createMetastaticProstateClinicDefaultFormValues,
} from '@ldt/features/orders/metastatic_prostate_clinic_form';
import {
  createMetastaticProstateTempusCustomResolver,
  createMetastaticProstateTempusDefaultFormValues,
} from '@ldt/features/orders/metastatic_prostate_tempus_form';
import {
  createProstateBiopsyCustomResolver,
  createProstateBiopsyDefaultFormValues,
  LdtProstateBiopsyFormData,
} from '@ldt/features/orders/prostate_biopsy_form';
import {
  createProstatePostRPCustomResolver,
  createProstatePostRPDefaultFormValues,
} from '@ldt/features/orders/prostate_post_rp_form';
import { canUseClinicalNotProvided } from '@ldt/features/orders/utils/clinical_not_provided';
import { AuthenticationService } from 'src/api/authentication_service';
import PaperClipIcon from 'src/assets/paper_clip.svg';
import { FieldDisplay } from 'src/components/field_display';
import { FieldDefinition } from 'src/components/field_display';
import { SectionHeader } from 'src/components/section_header';
import { getOrderPatientFullName } from 'src/distribution_platforms/features/utils/patient_utils';
import {
  LabType,
  LdtOrderDetailDirectUploadLabTechFieldsFragment,
  LdtOrderDetailFieldsFragment,
  OrderingPathway,
  PatientStatusAtBiopsy,
  PrimaryDiagnosisCode,
  MetastaticDiagnosisCode,
  SpecimenType,
  LdtUploadedFileFieldsFragment,
  UploadedFileStatus,
} from 'src/types';
import { UploadedFileCategory } from 'src/types';
import { UserRole } from 'src/types/user_roles';
import { WorkflowType } from 'src/types/workflows';
import {
  notifyError,
  NccnRiskCategoryUtils,
  TStageUtils,
  SurgicalMarginUtils,
  EthnicityUtils,
  PatientStatusAtBiopsyUtils,
  PrimaryDiagnosisCodeUtils,
  MetastaticDiagnosisCodeUtils,
  SpecimenTypeUtils,
} from 'src/utils';
import { formatLocaleDate, parseIsoDateString } from 'src/utils/date_parsing';

export const OrderInformationSection: FC<OrderInformationSectionProps> = ({
  order,
  orderRepository,
  userRoles = [],
  workflowType,
  isEditingQCPendingOrder = false,
}) => {
  const { isCSOrSalesUser, isLabTech } = deriveFromUserRoles(userRoles);
  const { getFilesForCategory } = useLdtFileUploads({
    order,
    orderRepository,
  });
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!isCSOrSalesUser) return;

    getValidationErrors(
      order,
      isCSOrSalesUser,
      workflowType,
      isEditingQCPendingOrder,
    ).then((errors) => {
      setValidationErrors(errors);
    });
  }, [order, isCSOrSalesUser, workflowType, isEditingQCPendingOrder]);

  function renderFullView() {
    const patientInfo = getPatientInfo(order, validationErrors);
    const clinicalInfo = getClinicalInfo(
      order,
      isCSOrSalesUser,
      validationErrors,
    );
    const specimenInfo = getSpecimenInfo(
      order,
      workflowType,
      isCSOrSalesUser,
      validationErrors,
    );
    const billingInfo = getBillingInfo(
      order,
      workflowType,
      isCSOrSalesUser,
      validationErrors,
    );

    const trfFiles = getFilesForCategory(
      UploadedFileCategory.TestRequisitionForm,
    );
    const faceSheetFiles = getFilesForCategory(UploadedFileCategory.FaceSheet);
    const pathologyReportFiles = getFilesForCategory(
      UploadedFileCategory.PathologyReport,
    );
    const recentOfficeNotesFiles = getFilesForCategory(
      UploadedFileCategory.RecentOfficeNote,
    );
    const insuranceFiles = getFilesForCategory(UploadedFileCategory.Insurance);
    const consultReportFiles = getFilesForCategory(
      UploadedFileCategory.ConsultReport,
    );
    const attestationFiles = getFilesForCategory(
      UploadedFileCategory.Attestation,
    );
    const psaReportFiles = getFilesForCategory(UploadedFileCategory.PsaReport);
    const otherFiles = getFilesForCategory(UploadedFileCategory.Other);

    const fileCategories = [
      {
        title: 'Demographic/Face Sheet',
        files: faceSheetFiles,
      },
      {
        title: 'Pathology Report',
        files: pathologyReportFiles,
      },
      {
        title: 'Most Recent Office Note',
        files: recentOfficeNotesFiles,
      },
      {
        title: 'Insurance Card(s)',
        files: insuranceFiles,
      },
      {
        title: 'PSA report',
        files: psaReportFiles,
      },
      { title: 'Consult Report', files: consultReportFiles },
      { title: 'Attestation', files: attestationFiles },
      {
        title: 'Test Requisition Form',
        files: trfFiles,
      },
      ...(isCSOrSalesUser ? [{ title: 'Other', files: otherFiles }] : []),
    ].filter((category) => category.files.length > 0);

    return (
      <Box>
        <Stack spacing={3}>
          {patientInfo.length > 0 && (
            <>
              <FormSectionContainer title='Patient Information'>
                <FormFieldsGrid fields={patientInfo} />
              </FormSectionContainer>
              <Divider />
            </>
          )}

          {clinicalInfo.length > 0 && (
            <>
              <FormSectionContainer title='Clinical Information'>
                <FormFieldsGrid fields={clinicalInfo} />
              </FormSectionContainer>
              <Divider />
            </>
          )}

          {specimenInfo && specimenInfo.length > 0 && (
            <>
              <FormSectionContainer title='Specimen Information'>
                <FormFieldsGrid fields={specimenInfo} />
              </FormSectionContainer>
              <Divider />
            </>
          )}

          {billingInfo.length > 0 && (
            <>
              <FormSectionContainer title='Billing Information'>
                <FormFieldsGrid fields={billingInfo} />
              </FormSectionContainer>
              {fileCategories.length > 0 && <Divider />}
            </>
          )}

          {fileCategories.map((category) => (
            <FormSectionContainer key={category.title} title={category.title}>
              <UploadedFilesList
                files={category.files}
                orderRepository={orderRepository}
              />
            </FormSectionContainer>
          ))}
        </Stack>
      </Box>
    );
  }

  function renderLabTechView() {
    return (
      <LabTechOrderInformationView
        order={order as LdtOrderDetailDirectUploadLabTechFieldsFragment}
      />
    );
  }

  if (
    [
      WorkflowType.ANCILLARY_LAB,
      WorkflowType.CLINIC,
      WorkflowType.TEMPUS,
    ].includes(workflowType)
  ) {
    return renderFullView();
  }

  if (workflowType === WorkflowType.DIRECT_UPLOAD_LAB) {
    if (isLabTech) {
      return renderLabTechView();
    } else if (isCSOrSalesUser) {
      return renderFullView();
    }
  }

  throw new Error(
    `Unsupported workflow type for order information: ${workflowType}`,
  );
};

/** Derive isCSOrSalesUser and isLabTech from user roles. */
function deriveFromUserRoles(userRoles: UserRole[] = []) {
  const isCSOrSalesUser = userRoles.some(
    (r) => r === UserRole.ArteraCustomerSuccess || r === UserRole.ArteraSales,
  );
  const isLabTech = userRoles.includes(UserRole.LabTech);
  return { isCSOrSalesUser, isLabTech };
}

/**
 * Lab-tech Direct Upload view. Mounted only for that scenario; slide comes
 * from the Direct Upload lab tech detail selection set.
 */
const LabTechOrderInformationView: FC<{
  order: LdtOrderDetailDirectUploadLabTechFieldsFragment;
}> = ({ order }) => {
  const labTechFields = getLabTechOrderInfoFields(order);
  const labTechSpecimenFields = getLabTechSpecimenInfoFields(order);

  return (
    <Box>
      <Stack spacing={3}>
        <FormSectionContainer title='Patient Information'>
          <FormFieldsGrid fields={labTechFields} />
        </FormSectionContainer>
        {labTechSpecimenFields.length > 0 && (
          <>
            <Divider />
            <FormSectionContainer title='Specimen Information'>
              <FormFieldsGrid fields={labTechSpecimenFields} />
            </FormSectionContainer>
          </>
        )}
      </Stack>
    </Box>
  );
};

interface OrderInformationSectionProps {
  order: LdtOrderDetailFieldsFragment;
  orderRepository: LdtOrderRepository;
  workflowType: WorkflowType;
  /** User roles; used to derive CS/Sales behavior and lab-tech vs full view. */
  userRoles?: UserRole[];
  isEditingQCPendingOrder?: boolean;
}

/**
 * Get validation errors and warnings for the order.
 * Warnings are soft validation errors that don't block submission.
 * Errors take precedence over warnings.
 */
const getValidationErrors = async (
  order: LdtOrderDetailFieldsFragment,
  isCSOrSalesUser: boolean,
  workflowType: WorkflowType,
  isEditingQCPendingOrder: boolean,
  warningsRef?: {
    current: Partial<Record<keyof LdtProstateBiopsyFormData, string>>;
  },
): Promise<Record<string, string>> => {
  const allowTStageNotProvided = canUseClinicalNotProvided(
    isCSOrSalesUser,
    AuthenticationService.getInstance().getEffectiveOrganization().features
      .allowTStageNotProvided,
    workflowType,
  );

  if (order.specimenType === SpecimenType.ProstateBiopsy) {
    const formData = createProstateBiopsyDefaultFormValues(order);
    const resolver = createProstateBiopsyCustomResolver(
      isCSOrSalesUser,
      workflowType,
      order.orderingPathway,
      isEditingQCPendingOrder,
      warningsRef,
      allowTStageNotProvided,
    );
    const result = await resolver(
      formData,
      {},
      {
        fields: {},
        shouldUseNativeValidation: false,
      },
    );

    return mergeResolverValidation(result.warnings, result.errors);
  } else if (order.specimenType === SpecimenType.ProstatePostRp) {
    const formData = createProstatePostRPDefaultFormValues(order);
    const resolver = createProstatePostRPCustomResolver(
      isCSOrSalesUser,
      order.orderingPathway,
      isEditingQCPendingOrder,
      warningsRef,
      allowTStageNotProvided,
      workflowType,
    );
    const result = await resolver(
      formData,
      {},
      {
        fields: {},
        shouldUseNativeValidation: false,
      },
    );

    return mergeResolverValidation(result.warnings, result.errors);
  } else if (order.specimenType === SpecimenType.MetastaticProstateBiopsy) {
    if (
      [
        WorkflowType.CLINIC,
        WorkflowType.DIRECT_UPLOAD_LAB,
        WorkflowType.ANCILLARY_LAB,
      ].includes(workflowType)
    ) {
      const formData = createMetastaticProstateClinicDefaultFormValues(order);
      const resolver = createMetastaticProstateClinicCustomResolver(
        isCSOrSalesUser,
        order.orderingPathway ?? OrderingPathway.DefaultNonReflexive,
        isEditingQCPendingOrder,
        undefined,
        allowTStageNotProvided,
        workflowType,
      );
      const result = await resolver(
        formData,
        {},
        { fields: {}, shouldUseNativeValidation: false },
      );

      return mergeResolverValidation(result.warnings, result.errors);
    } else if (workflowType === WorkflowType.TEMPUS) {
      const formData = createMetastaticProstateTempusDefaultFormValues(order);
      const resolver = createMetastaticProstateTempusCustomResolver();
      const result = await resolver(
        formData,
        {},
        {
          fields: {},
          shouldUseNativeValidation: false,
        },
      );

      return mergeResolverValidation(result.warnings, result.errors);
    }

    throw new Error(`Unsupported workflow type: ${workflowType}`);
  } else {
    throw new Error(`Unsupported specimen type: ${order.specimenType}`);
  }
};

/**
 * Extract patient information fields from Order
 */
const getPatientInfo = (
  order: LdtOrderDetailFieldsFragment,
  validationErrors?: Record<string, string>,
): FieldDefinition[] => {
  const patient = order.patient;
  const fullName = getOrderPatientFullName(order);

  const fields: FieldDefinition[] = [
    {
      label: 'Patient Name',
      value: fullName,
      errorMessage:
        validationErrors?.firstName || validationErrors?.lastName || null,
    },
  ];

  // Date of Birth: show if present
  const patientDateOfBirth = parseIsoDateString(
    patient?.dateOfBirthPhi ?? null,
  );
  fields.push({
    label: 'Date of Birth',
    value: patientDateOfBirth ? formatLocaleDate(patientDateOfBirth) : null,
    errorMessage: validationErrors?.dateOfBirth || null,
  });

  fields.push({
    label: 'Ethnicity',
    value: patient?.ethnicity
      ? EthnicityUtils.getEthnicityName(patient.ethnicity)
      : null,
    errorMessage: validationErrors?.ethnicity || null,
  });

  return fields.filter(
    (field) =>
      field.errorMessage || (field.value !== null && field.value !== undefined),
  );
};

/**
 * Extract lab tech order info: Patient Name, DOB, Clinic Name, Ordering Physician
 */
const getLabTechOrderInfoFields = (
  order: LdtOrderDetailFieldsFragment,
): FieldDefinition[] => {
  const patient = order.patient;
  const fullName = getOrderPatientFullName(order);
  const patientDateOfBirth = parseIsoDateString(
    patient?.dateOfBirthPhi ?? null,
  );
  const orderingPhysician = order.orderingProvider?.orderingPhysician;
  const orderingPhysicianName =
    orderingPhysician &&
    [orderingPhysician.firstName, orderingPhysician.lastName]
      .filter(Boolean)
      .join(' ');

  const fields: FieldDefinition[] = [
    { label: 'Patient Name', value: fullName },
    {
      label: 'Date of Birth',
      value: patientDateOfBirth ? formatLocaleDate(patientDateOfBirth) : null,
    },
    {
      label: 'Clinic Name',
      value: order.organizationName ?? null,
    },
    {
      label: 'Ordering Physician',
      value: orderingPhysicianName ?? null,
    },
  ];
  return fields.filter(
    (field) =>
      field.value !== null && field.value !== undefined && field.value !== '',
  );
};

/**
 * Sample Part and Accession ID — Tempus CS/Sales specimen view (unchanged labels).
 */
const getTempusSamplePartAndAccessionFields = (
  order: LdtOrderDetailFieldsFragment,
): FieldDefinition[] => [
  {
    label: 'Sample Part',
    value: order.slideSamplePart,
  },
  {
    label: 'Accession ID',
    value: order.patientLabAccession,
  },
];

/**
 * Specimen Information fields for Clinic, Ancillary Lab, and Direct Upload Lab.
 * Visible to all users. Sample Part is omitted for Post-RP.
 */
const getSpecimenInformationFields = (
  order: LdtOrderDetailFieldsFragment,
  validationErrors?: Record<string, string>,
): FieldDefinition[] => {
  const specimenType = order.specimenType;
  const procedureDate = order.labProcedureDatePhi
    ? parseIsoDateString(order.labProcedureDatePhi)
    : null;

  const fields: FieldDefinition[] = [
    {
      label: SpecimenTypeUtils.getSpecimenTypeInputLabel(specimenType),
      value: procedureDate ? formatLocaleDate(procedureDate) : null,
      errorMessage: validationErrors?.dateOfProcedure || null,
    },
    {
      label: 'Patient Lab Accession',
      value: order.patientLabAccession,
      errorMessage: validationErrors?.patientLabAccession || null,
    },
  ];

  if (
    specimenType === SpecimenType.ProstateBiopsy ||
    specimenType === SpecimenType.MetastaticProstateBiopsy
  ) {
    fields.push({
      label: 'Sample Part',
      value: order.slideSamplePart,
      errorMessage: validationErrors?.slideSamplePart || null,
    });
  }

  return fields;
};

/**
 * Extract specimen information fields shown in lab-tech (Direct Upload Lab) view.
 */
const getLabTechSpecimenInfoFields = (
  order: LdtOrderDetailDirectUploadLabTechFieldsFragment,
): FieldDefinition[] => {
  const fields: FieldDefinition[] = [
    {
      label: 'Specimen ID',
      value: order.specimenId ?? null,
    },
    {
      label: 'Sample Part',
      value: order.slideSamplePart,
    },
    {
      label: 'Patient Lab Accession',
      value: order.patientLabAccession,
    },
    {
      label: 'Site label',
      value: order.slide?.siteLabel ?? null,
    },
    {
      label: 'Block ID',
      value: order.slide?.blockId ?? null,
    },
  ];

  return fields.filter(
    (field) =>
      field.value !== null && field.value !== undefined && field.value !== '',
  );
};

/**
 * Extract clinical information fields from Order
 */
const getClinicalInfo = (
  order: LdtOrderDetailFieldsFragment,
  isCSOrSalesUser: boolean,
  validationErrors?: Record<string, string>,
): FieldDefinition[] => {
  const specimenType = order.specimenType;
  const fields: FieldDefinition[] = [];

  // Handle different specimen types
  if (specimenType === SpecimenType.ProstatePostRp) {
    fields.push(
      {
        label: 'Primary Gleason Score',
        value: order.primaryGleasonScore
          ? String(order.primaryGleasonScore)
          : null,
        errorMessage: validationErrors?.primaryGleasonScore,
      },
      {
        label: 'Secondary Gleason Score',
        value: order.secondaryGleasonScore
          ? String(order.secondaryGleasonScore)
          : null,
        errorMessage: validationErrors?.secondaryGleasonScore,
      },
      {
        label: 'Most recent PSA after prostatectomy (ng/mL)',
        value: order.psa?.toString(),
        errorMessage: validationErrors?.baselinePsa,
      },
      {
        label: 'Pathologic T-stage',
        value: order.tStage
          ? TStageUtils.getProstatePostRpTStageName(order.tStage)
          : null,
        errorMessage: validationErrors?.tStage,
      },
      {
        label: 'Surgical Margin',
        value: order.surgicalMargin
          ? SurgicalMarginUtils.getSurgicalMarginName(order.surgicalMargin)
          : null,
      },
    );
  } else if (specimenType === SpecimenType.ProstateBiopsy) {
    fields.push(
      {
        label: 'Primary Gleason Score',
        value: order.primaryGleasonScore
          ? String(order.primaryGleasonScore)
          : null,
        errorMessage: validationErrors?.primaryGleasonScore,
      },
      {
        label: 'Secondary Gleason Score',
        value: order.secondaryGleasonScore
          ? String(order.secondaryGleasonScore)
          : null,
        errorMessage: validationErrors?.secondaryGleasonScore,
      },
      {
        label: 'Most Recent Pre-Biopsy PSA (ng/mL)',
        value: order.psa?.toString(),
        errorMessage: validationErrors?.baselinePsa,
      },
      {
        label: 'T-stage',
        value: order.tStage
          ? TStageUtils.getProstateBiopsyTStageName(order.tStage)
          : null,
        errorMessage: validationErrors?.tStage,
      },
      {
        label: 'NCCN Risk Category',
        value: order.nccnRiskCategory
          ? NccnRiskCategoryUtils.getNccnRiskCategoryName(
              order.nccnRiskCategory,
            )
          : null,
        errorMessage: validationErrors?.nccnRiskCategory,
      },
    );

    if (isCSOrSalesUser) {
      fields.push(
        {
          label: 'Total Cores',
          value: order.totalCores?.toString(),
          errorMessage: validationErrors?.totalCores,
        },
        {
          label: 'Positive Cores',
          value: order.positiveCores?.toString(),
          errorMessage: validationErrors?.positiveCores,
        },
      );
    }
  } else if (specimenType === SpecimenType.MetastaticProstateBiopsy) {
    fields.push(
      {
        label: 'Most Recent Pre-Treatment PSA (ng/mL)',
        value: order.psa?.toString(),
        errorMessage: validationErrors?.baselinePsa,
      },
      {
        label: 'T-stage',
        value: order.tStage
          ? TStageUtils.getProstateBiopsyTStageName(order.tStage)
          : null,
        errorMessage: validationErrors?.tStage,
      },
    );
  }

  return fields.filter(
    (field) =>
      field.errorMessage || (field.value !== null && field.value !== undefined),
  );
};

/**
 * Extract specimen information fields from Order
 */
const getSpecimenInfo = (
  order: LdtOrderDetailFieldsFragment,
  workflowType: WorkflowType,
  isCSOrSalesUser: boolean,
  validationErrors?: Record<string, string>,
): FieldDefinition[] => {
  const fields: FieldDefinition[] = [];
  const specimenType = order.specimenType;

  const workflowTypesWithSpecimenId = [
    WorkflowType.ANCILLARY_LAB,
    WorkflowType.DIRECT_UPLOAD_LAB,
    WorkflowType.TEMPUS,
  ];

  // Specimen ID: show for CS/Sales when Ancillary Lab workflow or Direct Upload Lab order
  if (
    isCSOrSalesUser &&
    (workflowTypesWithSpecimenId.includes(workflowType) ||
      order.labType === LabType.DirectUploadLab)
  ) {
    fields.push({
      label: 'Specimen ID',
      value: order.specimenId,
    });
  }

  if (workflowType === WorkflowType.TEMPUS) {
    if (specimenType === SpecimenType.MetastaticProstateBiopsy) {
      if (isCSOrSalesUser) {
        fields.push(...getTempusSamplePartAndAccessionFields(order));

        const procedureDate = order.labProcedureDatePhi
          ? parseIsoDateString(order.labProcedureDatePhi)
          : null;
        fields.push({
          label: SpecimenTypeUtils.getSpecimenTypeInputLabel(specimenType),
          value: procedureDate ? formatLocaleDate(procedureDate) : null,
          errorMessage: validationErrors?.dateOfProcedure,
        });
      }
    }
  } else {
    // Clinic, Ancillary Lab, Direct Upload Lab: visible to all users
    fields.push(...getSpecimenInformationFields(order, validationErrors));
  }

  return fields.filter(
    (field) =>
      field.errorMessage || (field.value !== null && field.value !== undefined),
  );
};

/**
 * Extract billing information fields from Order
 */
const getBillingInfo = (
  order: LdtOrderDetailFieldsFragment,
  workflowType: WorkflowType,
  isCSOrSalesUser: boolean,
  validationErrors?: Record<string, string>,
): FieldDefinition[] => {
  const fields: FieldDefinition[] = [];

  if (
    [
      SpecimenType.ProstateBiopsy,
      SpecimenType.ProstatePostRp,
      SpecimenType.MetastaticProstateBiopsy,
    ].includes(order.specimenType)
  ) {
    fields.push({
      label: 'Patient Status (at the time of biopsy)',
      value: order.patientStatusAtBiopsy
        ? PatientStatusAtBiopsyUtils.getPatientStatusAtBiopsyName(
            order.patientStatusAtBiopsy,
          )
        : null,
      errorMessage: validationErrors?.patientStatus || null,
    });

    // Date of Discharge: show if patient status is hospital inpatient or outpatient
    const isHospitalStatus =
      order.patientStatusAtBiopsy === PatientStatusAtBiopsy.HospitalInpatient ||
      order.patientStatusAtBiopsy === PatientStatusAtBiopsy.HospitalOutpatient;
    if (isHospitalStatus) {
      const dischargeDate = parseIsoDateString(order.patientDischargeDate);
      fields.push({
        label: 'Date of Discharge',
        value: dischargeDate ? formatLocaleDate(dischargeDate) : null,
        errorMessage: validationErrors?.dateOfDischarge || null,
      });
    }

    const primaryDiagnosisCodeNames: string[] = [];

    if (order.primaryDiagnosisCodes?.includes(PrimaryDiagnosisCode.C61)) {
      primaryDiagnosisCodeNames.push(
        PrimaryDiagnosisCodeUtils.getPrimaryDiagnosisCodeName(
          PrimaryDiagnosisCode.C61,
        ),
      );
    }

    if (order.customPrimaryDiagnosisCode) {
      primaryDiagnosisCodeNames.push(order.customPrimaryDiagnosisCode);
    }

    const primaryDiagnosisCodes = primaryDiagnosisCodeNames.join(', ');

    fields.push({
      label: 'ICD-10 Primary Diagnosis Code(s)',
      value: primaryDiagnosisCodes,
      errorMessage:
        validationErrors?.primaryDiagnosisCodes ||
        validationErrors?.customPrimaryDiagnosisCode ||
        null,
    });

    if (order.specimenType === SpecimenType.MetastaticProstateBiopsy) {
      const metastaticDiagnosisCodeNames: string[] = [];

      if (
        order.metastaticDiagnosisCodes?.includes(MetastaticDiagnosisCode.C79_51)
      ) {
        metastaticDiagnosisCodeNames.push(
          MetastaticDiagnosisCodeUtils.getMetastaticDiagnosisCodeName(
            MetastaticDiagnosisCode.C79_51,
          ),
        );
      }

      if (order.customMetastaticDiagnosisCode) {
        metastaticDiagnosisCodeNames.push(order.customMetastaticDiagnosisCode);
      }

      fields.push({
        label: 'ICD-10 Metastatic Diagnosis Code(s)',
        value: metastaticDiagnosisCodeNames.join(', '),
        errorMessage:
          (validationErrors as Record<string, string> | undefined)
            ?.metastaticDiagnosisCodes ||
          (validationErrors as Record<string, string> | undefined)
            ?.customMetastaticDiagnosisCode ||
          null,
      });
    }

    if (isCSOrSalesUser && workflowType === WorkflowType.ANCILLARY_LAB) {
      fields.push({
        label: 'Consent to Bill Patient',
        value: order.consentToBillPatient ? 'Yes' : 'No',
      });
    }
  }

  return fields.filter(
    (field) =>
      field.errorMessage || (field.value !== null && field.value !== undefined),
  );
};

/**
 * Form fields grid component that displays fields horizontally first,
 * then wraps to the next row. Two columns on medium+ screens, single column on mobile.
 * @param fields - The fields to display
 * @returns The FormFieldsGrid component
 */
const FormFieldsGrid: FC<{ fields: FieldDefinition[] }> = ({ fields }) => {
  if (fields.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      {fields.map((field, index) => (
        <Grid key={index} size={{ xs: 12, md: 6 }}>
          <FieldDisplay field={field} variant='vertical' />
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * Form Section Container component
 */
const FormSectionContainer: FC<{
  title: string;
  children: ReactNode;
}> = ({ title, children }) => {
  return (
    <Box>
      <SectionHeader title={title} />
      {children}
    </Box>
  );
};

/**
 * Uploaded Files List component for read-only display with download functionality
 */
const UploadedFilesList: FC<{
  files: LdtUploadedFileFieldsFragment[];
  orderRepository: LdtOrderRepository;
}> = ({ files, orderRepository }) => {
  const theme = useTheme();
  const handleDownloadFile = useCallback(
    async (file: LdtUploadedFileFieldsFragment) => {
      // Only allow download for completed files
      if (file.status !== UploadedFileStatus.Complete) {
        return;
      }

      try {
        const downloadUrl = await orderRepository.getFileDownloadUrl(file.id);
        // Open the presigned URL in a new tab to download the file
        window.open(downloadUrl, '_blank');
      } catch (_) {
        notifyError('Failed to download file. Please try again.');
      }
    },
    [orderRepository],
  );

  if (files.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {files.map((file, index) => {
        const isDownloadable = file.status === UploadedFileStatus.Complete;
        return (
          <Button
            key={file.id || index}
            variant='text'
            startIcon={<img src={PaperClipIcon} alt='Paper Clip' />}
            onClick={() => handleDownloadFile(file)}
            disabled={!isDownloadable}
            sx={{
              textTransform: 'none',
              justifyContent: 'flex-start',
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: 0,
              backgroundColor:
                theme.palette.mode === 'light'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[600],
            }}
          >
            <Typography variant='body2' color={theme.palette.text.primary}>
              {file.filename}
            </Typography>
          </Button>
        );
      })}
    </Box>
  );
};

const mergeResolverValidation = (
  warnings: Partial<Record<string, string>>,
  errors: Partial<Record<string, { message?: string }>>,
): Record<string, string> => {
  const fieldMessages: Record<string, string> = {};

  Object.entries(warnings).forEach(([fieldName, warning]) => {
    if (warning) {
      fieldMessages[fieldName] = warning;
    }
  });
  Object.entries(errors).forEach(([fieldName, error]) => {
    if (error?.message) {
      fieldMessages[fieldName] = error.message;
    }
  });

  return fieldMessages;
};
